import { useState } from "react";
import { ChevronRight, FileText, Folder } from "lucide-react";
import type { NoteTreeNode } from "@/api/types";
import { cn } from "@/lib/utils";

/** Radio-style picker over a space's page tree. `null` means top level. */
export function TreePicker({
  tree,
  value,
  onChange,
  excludeId,
  loading,
}: {
  tree: NoteTreeNode[] | undefined;
  value: string | null;
  onChange: (id: string | null) => void;
  /** Hide this note and its descendants (a note cannot be moved under itself). */
  excludeId?: string;
  loading?: boolean;
}) {
  return (
    <div className="max-h-72 overflow-y-auto rounded-lg border bg-card p-1" role="listbox" aria-label="Parent page">
      <Row selected={value === null} onSelect={() => onChange(null)} depth={0} icon={<Folder className="size-4" />} label="Top level" />
      {loading && <div className="px-2 py-2 text-xs text-muted-foreground">Loading pages…</div>}
      {(tree ?? []).filter((n) => n.id !== excludeId).map((n) => (
        <Node key={n.id} node={n} depth={1} value={value} onChange={onChange} excludeId={excludeId} />
      ))}
    </div>
  );
}

function Node({
  node,
  depth,
  value,
  onChange,
  excludeId,
}: {
  node: NoteTreeNode;
  depth: number;
  value: string | null;
  onChange: (id: string | null) => void;
  excludeId?: string;
}) {
  const kids = node.children?.filter((c) => c.id !== excludeId) ?? [];
  const [open, setOpen] = useState(depth < 2);
  return (
    <>
      <Row
        selected={value === node.id}
        onSelect={() => onChange(node.id)}
        depth={depth}
        icon={<FileText className="size-4 opacity-70" />}
        label={node.title || "Untitled"}
        toggle={
          kids.length > 0 ? (
            <button
              type="button"
              className="flex size-5 items-center justify-center text-muted-foreground hover:text-foreground"
              onClick={(e) => {
                e.stopPropagation();
                setOpen((o) => !o);
              }}
              aria-label={open ? "Collapse" : "Expand"}
            >
              <ChevronRight className={cn("size-3.5 transition-transform", open && "rotate-90")} />
            </button>
          ) : (
            <span className="size-5" />
          )
        }
      />
      {open && kids.map((c) => <Node key={c.id} node={c} depth={depth + 1} value={value} onChange={onChange} excludeId={excludeId} />)}
    </>
  );
}

function Row({
  selected,
  onSelect,
  depth,
  icon,
  label,
  toggle,
}: {
  selected: boolean;
  onSelect: () => void;
  depth: number;
  icon: React.ReactNode;
  label: string;
  toggle?: React.ReactNode;
}) {
  return (
    <div
      role="option"
      aria-selected={selected}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        "flex cursor-pointer items-center gap-1.5 rounded-md py-1.5 pr-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
        selected ? "bg-primary/[0.08] font-medium" : "hover:bg-muted",
      )}
      style={{ paddingLeft: 6 + depth * 14 }}
    >
      {toggle ?? <span className="size-5" />}
      {icon}
      <span className="truncate">{label}</span>
    </div>
  );
}
